import axios from 'axios';
import {
	ENDPOINT_URL,
	GET_SSID_SCHEDULER_LIST,
} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	optionalText,
	resolveCandidatesWithFallback,
	responseRows,
} from '../../core/resolutionUtils';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * Return an SSID Schedule row identifier.
 * @param {object} item Canvas Schedule row
 * @return {*} identifier
 */
function scheduleId(item) {
	return item.id ?? item.schedule_id;
}

/**
 * Normalize a Canvas SSID Schedule row.
 * @param {object} item Canvas Schedule row
 * @return {object|null} agent candidate
 */
function normalizeSchedule(item) {
	const id = scheduleId(item);
	const name = item.profile_name ?? item.name;
	if (id == null || !name) return null;
	return {
		entity_type: 'schedule',
		entity_id: String(id),
		name: String(name),
		site_id: null,
		site_name: null,
		security_type: optionalText(item.auth_type),
		status: optionalText(item.status),
		serial_no: null,
	};
}

/**
 * Resolve SSID Schedule candidates.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveSsidSchedules(params) {
	const query = String(params.query || '').trim();
	// `browse` lists the scope instead of matching a name, so a miss can answer
	// with the SSID Schedules that do exist. Named lookups are unchanged.
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	const loadRows = async (search, limit) => {
		const searchParam = search
			? `&search=${encodeURIComponent(search)}`
			: '';
		const url = `${ENDPOINT_URL}${GET_SSID_SCHEDULER_LIST}?limit=${limit}` +
			`&offset=0${searchParam}`;
		const {data} = await axios.get(url);
		return responseRows(decodeAgentResponse(data) || {});
	};
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('schedule', rows, scheduleId);
		return {
			candidates: rows.map(normalizeSchedule).filter(Boolean).slice(0, limit),
		};
	}
	const {rows, candidates} = await resolveCandidatesWithFallback({
		query,
		loadRows,
		normalizeRow: normalizeSchedule,
		fuzzy: params.fuzzy === true,
	});
	replaceResolvedEntities('schedule', rows, scheduleId);
	return {candidates};
}

/**
 * Return a retained SSID Schedule row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedSsidSchedule(entityId) {
	return getResolvedEntity('schedule', entityId);
}
